import { IconButton } from '@mui/material';
import clsx from 'clsx';
import { Link } from 'react-router-dom';

import { Avatar, Icon, Skeleton, Typography } from '@/ui';

import type {
  MomentsSavedHighlight,
  MomentsSavedHighlightSort,
} from '@/store/api/momentsApi';
import type { FC } from 'react';

import {
  formatSavedHighlightDate,
  getSavedHighlightPath,
} from '@/components/Home/SessionReflections/savedHighlightsHomeUtils';

export type MemberHighlightsPageStatus =
  'error' | 'loading' | 'ready' | 'search-empty';

export interface MemberHighlightsMember {
  avatarUrl?: string;
  firstName?: string;
  lastName?: string;
  name?: string;
}

export interface MemberHighlightsPageViewProps {
  emptyHighlightHref?: string;
  hasMore?: boolean;
  highlights: MomentsSavedHighlight[];
  isLoadingMore?: boolean;
  isRefreshing?: boolean;
  member?: MemberHighlightsMember;
  onBack?: () => void;
  onLoadMore?: () => void;
  onSearchChange: (value: string) => void;
  onSortChange: (value: MomentsSavedHighlightSort) => void;
  searchTerm: string;
  showControls?: boolean;
  showManageSavedNotes?: boolean;
  sort: MomentsSavedHighlightSort;
  status?: MemberHighlightsPageStatus;
}

const sortOptions: Array<{
  label: string;
  value: MomentsSavedHighlightSort;
}> = [
  { label: 'Last added', value: 'created_at' },
  { label: 'By session date', value: 'session_date' },
];

const HighlightIllustration = () => (
  <div aria-hidden="true" className="relative h-[76px] w-[249px]">
    <div className="absolute left-0 top-[33px] h-3 w-[205px] rounded-lg bg-sunny-300" />
    <div className="absolute left-0 top-[17px] h-3 w-[230px] rounded-lg bg-sunny-300" />
    <div className="absolute left-[154px] top-px h-3 w-[72px] rounded-r-lg bg-sunny-300" />
    <div className="absolute left-0 top-[49px] h-3 w-[111px] rounded-l-lg bg-sunny-300" />

    <div className="absolute left-1 top-1 flex gap-1">
      {[18, 18, 45, 39, 12, 35, 27].map((width, index) => (
        <span
          // eslint-disable-next-line react/no-array-index-key -- Decorative fixed-width marks.
          key={index}
          className="h-1.5 rounded-lg bg-cement-200"
          style={{ width }}
        />
      ))}
    </div>
    <div className="absolute left-1 top-5 flex gap-1">
      {[45, 18, 12, 43, 18, 66].map((width, index) => (
        <span
          // eslint-disable-next-line react/no-array-index-key -- Decorative fixed-width marks.
          key={index}
          className="h-1.5 rounded-lg bg-cement-200"
          style={{ width }}
        />
      ))}
    </div>
    <div className="absolute left-1 top-9 flex gap-1">
      {[66, 12, 45, 40, 18].map((width, index) => (
        <span
          // eslint-disable-next-line react/no-array-index-key -- Decorative fixed-width marks.
          key={index}
          className="h-1.5 rounded-lg bg-cement-200"
          style={{ width }}
        />
      ))}
    </div>
    <div className="absolute left-1 top-[52px] flex gap-1">
      {[12, 18, 45, 18, 43, 30, 18].map((width, index) => (
        <span
          // eslint-disable-next-line react/no-array-index-key -- Decorative fixed-width marks.
          key={index}
          className="h-1.5 rounded-lg bg-cement-200"
          style={{ width }}
        />
      ))}
    </div>
    <div className="absolute left-1 top-[68px] flex gap-1">
      {[45, 18, 18].map((width, index) => (
        <span
          // eslint-disable-next-line react/no-array-index-key -- Decorative fixed-width marks.
          key={index}
          className="h-1.5 rounded-lg bg-cement-200"
          style={{ width }}
        />
      ))}
    </div>

    <div className="absolute left-[201px] top-12 rounded-[9px] border border-solid border-cement-200 bg-white px-2.5 py-1.5 shadow-md">
      <span className="text-xs font-bold leading-[16px] text-cement-400">
        Save
      </span>
    </div>
    <div className="absolute left-[153px] top-px h-3 w-px bg-sunny-500" />
    <div className="absolute left-[152px] top-0 size-[3px] rounded-sm bg-sunny-500" />
    <div className="absolute left-[110px] top-[60px] size-[3px] rounded-sm bg-sunny-500" />
    <div className="absolute left-[111px] top-[49px] h-3 w-px bg-sunny-500" />
  </div>
);

const LoadingCards = () => (
  <div className="flex flex-col gap-8">
    {[0, 1, 2].map((index) => (
      <Skeleton key={index} className="h-[210px] w-full" variant="rounded" />
    ))}
  </div>
);

const HighlightCard: FC<{
  highlight: MomentsSavedHighlight;
  member?: MemberHighlightsMember;
}> = ({ highlight, member }) => {
  const href = getSavedHighlightPath(highlight);
  const dateLabel = formatSavedHighlightDate(highlight.createdAt);
  const memberName =
    member?.name ||
    [member?.firstName, member?.lastName].filter(Boolean).join(' ') ||
    'Member';
  const hasNote = highlight.note.trim().length > 0;

  return (
    <article className="rounded-lg border border-solid border-cement-200 bg-white shadow-sm transition-shadow focus-within:shadow-md hover:shadow-md">
      <Link
        aria-label={`View highlight in reflection: ${highlight.selectedText}`}
        className="block p-6 text-cement-500 no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage-500"
        to={href}
      >
        <p className="m-0 text-xl font-medium leading-[1.6] tracking-[0.15px] text-cement-500">
          {highlight.selectedText}
        </p>
        <div className="my-6 h-px bg-cement-200" />
        <div className="flex flex-col gap-2.5">
          <div className="flex items-center gap-2.5">
            <Avatar
              firstName={member?.firstName}
              lastName={member?.lastName}
              size="s"
              src={member?.avatarUrl}
            />
            <Typography className="text-black" variant="body2">
              {memberName}
            </Typography>
          </div>
          {hasNote ? (
            <Typography className="text-black" variant="body2">
              {highlight.note}
            </Typography>
          ) : null}
          {dateLabel ? (
            <Typography className="text-cement-300" variant="caption">
              {hasNote ? `Private note • ${dateLabel}` : dateLabel}
            </Typography>
          ) : null}
        </div>
      </Link>
    </article>
  );
};

const EmptyHighlights = ({ href }: { href?: string }) => {
  const cta = (
    <span className="inline-flex min-h-9 items-center justify-center rounded-xl bg-sage-500 px-4 py-1.5 text-[13px] font-bold leading-6 tracking-[0.4px] text-white">
      Highlight something
    </span>
  );

  return (
    <div className="flex min-h-[374px] flex-col items-center justify-center rounded-2xl border border-solid border-cement-200 px-6 py-12 text-center">
      <HighlightIllustration />
      <div className="mt-6 flex max-w-[386px] flex-col items-center gap-1">
        <Typography className="text-cement-500" variant="h6">
          No highlights... yet
        </Typography>
        <Typography className="text-cement-500" variant="body1">
          Insights, action items, or anything else you save shows up here.
        </Typography>
      </div>
      {href ? (
        <Link className="mt-6 no-underline" to={href}>
          {cta}
        </Link>
      ) : (
        <button
          className="mt-6 border-0 bg-transparent p-0"
          type="button"
          disabled
        >
          {cta}
        </button>
      )}
    </div>
  );
};

const NoMatches = () => (
  <div className="rounded-2xl border border-solid border-cement-200 px-8 py-16 text-center">
    <Typography className="text-cement-400" variant="body1">
      No matches.
    </Typography>
  </div>
);

const ErrorState = () => (
  <div className="rounded-2xl border border-solid border-cement-200 px-8 py-16 text-center">
    <Typography className="text-cement-400" variant="body1">
      We couldn&apos;t load your highlights. Please try again later.
    </Typography>
  </div>
);

const MemberHighlightsPageView: FC<MemberHighlightsPageViewProps> = ({
  emptyHighlightHref,
  hasMore = false,
  highlights,
  isLoadingMore = false,
  isRefreshing = false,
  member,
  onBack,
  onLoadMore,
  onSearchChange,
  onSortChange,
  searchTerm,
  showControls,
  showManageSavedNotes = false,
  sort,
  status = 'ready',
}) => {
  const hasSearch = searchTerm.trim().length > 0;
  const shouldShowControls =
    showControls ?? (highlights.length > 0 || hasSearch);

  let content = (
    <div className="flex flex-col gap-8">
      {highlights.map((highlight) => (
        <HighlightCard
          key={highlight.id}
          highlight={highlight}
          member={member}
        />
      ))}
    </div>
  );
  if (status === 'loading') {
    content = <LoadingCards />;
  } else if (status === 'error') {
    content = <ErrorState />;
  } else if (status === 'search-empty') {
    content = <NoMatches />;
  } else if (highlights.length === 0) {
    content = <EmptyHighlights href={emptyHighlightHref} />;
  }

  return (
    <div className="relative min-h-screen bg-sunny-100 px-6 pb-16 pt-6">
      <IconButton
        aria-label="Back"
        className="!absolute !left-6 !top-6"
        size="small"
        onClick={onBack}
      >
        <Icon fontSize="small">arrow_back</Icon>
      </IconButton>

      <main className="mx-auto flex w-full max-w-[768px] flex-col pt-12 md:pt-14">
        <Typography className="mb-8 font-bold text-cement-500" variant="h4">
          Highlights
        </Typography>
        {showManageSavedNotes ? (
          <Link
            className="mb-6 text-sage-600"
            to="/m/highlights?view=owned-snapshots"
          >
            Manage saved notes
          </Link>
        ) : null}

        {shouldShowControls ? (
          <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:gap-8">
            <input
              aria-label="Search highlights"
              className="min-h-14 min-w-0 flex-1 rounded-lg border border-solid border-cement-200 bg-white px-4 font-sans text-base font-medium text-cement-500 outline-none placeholder:text-cement-400 focus:border-sage-500"
              placeholder="Search..."
              style={{ fontFamily: 'inherit' }}
              type="search"
              value={searchTerm}
              onChange={(event) => onSearchChange(event.target.value)}
            />
            <div className="flex shrink-0 items-center gap-5">
              {sortOptions.map((option) => {
                const isSelected = sort === option.value;
                return (
                  <button
                    key={option.value}
                    className={clsx(
                      'rounded border-0 bg-transparent text-sm tracking-[0.15px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage-500 md:text-base',
                      isSelected
                        ? 'font-bold text-cement-500'
                        : 'font-medium text-cement-400',
                    )}
                    style={{ fontFamily: 'inherit' }}
                    type="button"
                    onClick={() => onSortChange(option.value)}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}

        {content}

        {isRefreshing ? (
          <Typography className="mt-4 text-cement-300" variant="caption">
            Updating highlights…
          </Typography>
        ) : null}

        {hasMore && status === 'ready' ? (
          <div className="mt-8 text-center">
            <button
              className="inline-flex rounded-xl border border-solid border-cement-300 bg-transparent px-4 py-2 text-sm font-bold text-cement-500"
              disabled={isLoadingMore}
              style={{ fontFamily: 'inherit' }}
              type="button"
              onClick={onLoadMore}
            >
              {isLoadingMore ? 'Loading…' : 'Load more'}
            </button>
          </div>
        ) : null}
      </main>
    </div>
  );
};

export default MemberHighlightsPageView;
